import type { TokenTransferConfigResult } from '@funkit/connect-core'

/**
 * One curated deposit token (chain + asset), produced from the resolved
 * `tokentransferconfig`. Consumed by the deposit flow (its chains drive the
 * "Available chains" list).
 */
export interface SupportedToken {
  id: string
  symbol: string
  /** Chain shown on the trailing side, e.g. "Ethereum". */
  chainLabel: string
  /** Numeric chain id — used to derive the available-chains list + re-target. */
  chainId?: number
  /** Token contract address. */
  address?: string
  /**
   * Required for `lockAmount` unless curated metadata supplies precision for
   * this chain and address. Tokens without precision are excluded in locked flows.
   */
  decimals?: number
}

/** A curated chain entry. The first is usually `{ id: 'all', label: 'All' }`. */
export interface TokenChainFilter {
  id: string
  label: string
}

/**
 * Flatten a resolved token-transfer config (the curated Statsig
 * `tokentransferconfig` merged with `/assets/supported` metadata — see
 * `resolveTokenTransferConfig`) into the supported-tokens screen's token rows +
 * chain-filter chips. One row per curated (chain, asset); chains keep their
 * config order; priority symbols float to the top. NOT the raw allow-list, so
 * no meme coins — only the customer's configured deposit tokens.
 */
export function buildSupportedTokenList(resolved: TokenTransferConfigResult): {
  tokens: SupportedToken[]
  chainFilters: TokenChainFilter[]
} {
  const priorityIndex = new Map(
    resolved.priorityTokenSymbols.map((symbol, i) => [symbol, i]),
  )

  const tokens: SupportedToken[] = []
  const chainFilters: TokenChainFilter[] = [{ id: 'all', label: 'All' }]

  for (const chainId of resolved.chainIdSortOrder) {
    const assets = Object.values(resolved.supportedAssets[chainId] ?? {})
    if (assets.length === 0) {
      continue
    }
    chainFilters.push({ id: String(chainId), label: assets[0].chainName })
    for (const asset of assets) {
      tokens.push({
        id: `${asset.chainId}:${asset.address}`,
        symbol: asset.symbol,
        chainLabel: asset.chainName,
        chainId: Number(asset.chainId),
        address: asset.address,
        decimals: asset.decimals,
      })
    }
  }

  // Stable sort: priority symbols first (in priority order); everything else
  // keeps its chain/insertion order.
  const rank = (symbol: string) =>
    priorityIndex.get(symbol) ?? Number.POSITIVE_INFINITY
  tokens.sort((a, b) => rank(a.symbol) - rank(b.symbol))

  return { tokens, chainFilters }
}
